import * as React from 'react';
import { Avatar, Badge, Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import RacingParticipantResultDto from '../models/dtos/RacingParticipantResultDto';

interface RacingParticipantsTableProps {
    participants: RacingParticipantResultDto[];
    resultRaw?: string;
}

const getResultBadgeColor = (resultNumber: number | null): string => {
    if (resultNumber == null) return '#9e9e9e';
    if (resultNumber === 1) return '#4caf50';
    if (resultNumber === 2 || resultNumber === 3) return '#ff9800';
    return '#f44336';
};

export default function RacingParticipantsTable({ participants, resultRaw }: RacingParticipantsTableProps) {
    return (
        <React.Fragment>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Photo</TableCell>
                    <TableCell>Horse Name</TableCell>
                    <TableCell>Jockey Name</TableCell>
                    <TableCell>Skill</TableCell>
                    <TableCell>Result</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {participants.map((participant) => (
                    <TableRow key={participant.id}>
                        <TableCell>
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    border: '2px solid',
                                    borderColor: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography variant="body2" fontWeight="bold">
                                    {participant.sequence_number}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell>
                            {participant.photo_url ? (
                                <Avatar src={participant.photo_url} sx={{ width: 40, height: 40 }} />
                            ) : (
                                '—'
                            )}
                        </TableCell>
                        <TableCell>{participant.horse_name}</TableCell>
                        <TableCell>{participant.jockey_name ?? '—'}</TableCell>
                        <TableCell>{participant.skill.toFixed(2)}</TableCell>
                        <TableCell>
                            <Chip
                                icon={<FlagIcon sx={{ fontSize: 16, color: 'inherit' }} />}
                                label={participant.result_number ?? '—'}
                                size="small"
                                sx={{
                                    backgroundColor: getResultBadgeColor(participant.result_number),
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    '& .MuiChip-icon': {
                                        color: '#fff',
                                    },
                                }}
                            />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
        {resultRaw && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Raw result from provider
                </Typography>
                <Typography
                    variant="body2"
                    component="pre"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, fontFamily: 'monospace' }}
                >
                    {resultRaw}
                </Typography>
            </Box>
        )}
        </React.Fragment>
    );
}
